/**
 * Expensive CSS override: when the user opts in via localStorage, a global
 * stylesheet is injected that turns off costly CSS (transitions, filters,
 * shadows, blend modes, masks, etc.) to improve performance on low-GPU or
 * low-CPU devices.
 *
 * Enable overrides:  localStorage.setItem(EXPENSIVE_CSS_STORAGE_KEY, 'true')
 * Disable overrides: localStorage.setItem(EXPENSIVE_CSS_STORAGE_KEY, 'false')
 */ const OVERRIDE_STYLE_ID = 'prophecy-expensive-css-override';
export const EXPENSIVE_CSS_CLS = 'prophecy-expensive-css-disabled';
/** Global CSS that resets expensive properties to reduce paint/composite cost. */ const REDUCED_CSS = `
.${EXPENSIVE_CSS_CLS} {
    /* Transitions (including transition: all) */
    * { transition: none !important; }

    /* Filter effects (backdrop-filter, filter) */
    * { backdrop-filter: none !important; }
    * { filter: none !important; }

    /* Shadows */
    * { box-shadow: none !important; }
    * { text-shadow: none !important; }

    /* Blend modes */
    * { background-blend-mode: normal !important; }
    * { mix-blend-mode: normal !important; }

    /* Mask and clip */
    * { mask-image: none !important; }
    * { clip-path: none !important; }
}
`;
/** Returns the injected <style> element if present; null when absent or in SSR. */ function getStyleElement() {
    if (typeof document === 'undefined') return null;
    return document.getElementById(OVERRIDE_STYLE_ID);
}
/**
 * localStorage key for the "disable expensive CSS" preference.
 * Value 'true' = apply overrides (disable expensive CSS); 'false' or unset = do not override.
 */ export const EXPENSIVE_CSS_STORAGE_KEY = 'prophecy.disableExpensiveCss';
/**
 * Returns true when the user has opted to disable expensive CSS (overrides applied).
 * Safe in SSR and when localStorage is unavailable; returns false in those cases.
 */ function getDisableExpensiveCSS() {
    if (typeof localStorage === 'undefined') return false;
    try {
        return localStorage.getItem(EXPENSIVE_CSS_STORAGE_KEY) === 'true';
    } catch (e) {
        return false;
    }
}
/**
 * Applies or removes the expensive-CSS override stylesheet according to `enabled`.
 * When true, injects/updates the override; when false, removes it.
 * Intended to be called once at app bootstrap; safe to call multiple times.
 */ export function applyExpensiveCssOverrides(enabled) {
    if (typeof document === 'undefined') return;
    const existing = getStyleElement();
    if (enabled) {
        let css = REDUCED_CSS;
        if (existing) {
            existing.textContent = css;
        } else {
            const style = document.createElement('style');
            style.id = OVERRIDE_STYLE_ID;
            style.setAttribute('data-name', 'prophecy-expensive-css-override');
            style.textContent = css;
            if (window.__webpack_nonce__) {
                style.setAttribute('nonce', window.__webpack_nonce__);
            }
            document.head.appendChild(style);
        }
        document.body.classList.add(EXPENSIVE_CSS_CLS);
    } else if (existing) {
        existing.remove();
        document.body.classList.remove(EXPENSIVE_CSS_CLS);
    }
}
export function getIsExpensiveCssDisabled(globalConfig) {
    const rawValue = globalConfig['prophecy.ui.expensiveCss.disabled'];
    if (rawValue === true || rawValue === 'true') {
        return true;
    }
    if (rawValue === false || rawValue === 'false') {
        return false;
    }
    return getDisableExpensiveCSS();
}
